/*
 * aura-meerkat.js: today's discount sign-up and the Meerkat visitor events (snippets/meerkat.liquid),
 * carried over from the live theme's assets/marketingmodal.js and the script in sections/footer.liquid.
 *
 * <aura-meerkat>
 *   Sign-up: an email gets the full-roll code, then a phone number gets texts. Both go to the Marketing
 *   API's /events/user, and the id it returns stays on the device as `mkt`. The sale follows the API's
 *   holiday list (/hollist); between holidays it is the month's special, a 3-day window per visitor that
 *   starts again on its last day, as today. Closed, the modal shrinks into the discount button beside Ask
 *   Aura (in the mobile buy bar on product pages), which opens it again until a phone number is given. On a
 *   product page whose picked variant costs less than small_under, the button and the modal offer the
 *   partial code's percent off ("10% off anything under $400") instead of the full-roll dollars. While today's holiday has a shippingNotice,
 *   <html> gets .has-shipping-notice, which shows the offer band and the cart's shipping-notice line, and the
 *   shipping-notification page takes its text and dates from that holiday.
 *   Visitor events: the page and each cart line go to /events/meerkat once the visitor has an id; product
 *   pages wait in anon_mkt_list until then and are sent on sign-up. A signed-in customer is recorded once
 *   per device (lmkt) and their cart goes to /events/cart. Explorer links carry the id as ?mkt=.
 *   Discount unlocked: an email (here, or the footer signup through unlock()) marks prices down site-wide,
 *   as a wholesale login does. The sale's dollar amount goes on the cart as the attribute `_aura_unlock`,
 *   which snippets/unlock-price.liquid reads on every render: a full roll shows the dollars off, a small
 *   item the partial code's percent off. The page reloads into those prices when the modal closes. The
 *   code that fits the cart stays on it (the full-roll code with a full roll in it, else the partial code), and
 *   the discount button, the auto-open and "Get discount code" are gone for good (aura.discount.v1, and
 *   .is-unlocked on <html>). Visitors who gave a phone number on the live theme (mkt_phone) count too.
 *   Each page puts the amount back on the cart if it changed (a holiday) or went (a finished order).
 *   Code links: landing on ?discount=CODE (a campaign's /discount/CODE?redirect=/?discount=CODE, a text, any link)
 *   unlocks at once, no sign-up, at the better of that code and today's sale, reloading once into the prices. The
 *   code can be today's sale code, one of the API's link-only offers (/hollist `offers`, between its dates) or any
 *   other active Shopify code, which the API looks up (GET /discount/:code, once a session). The device keeps it
 *   (aura.link_offer.v1) until it ends or is used up. A percent-off code only goes on the cart.
 *   "Have a code?" (snippets/meerkat-code-form.liquid, on the email and code steps; the cart's "Have a different
 *   code?" opens it once unlocked) takes a typed code the same way: if it's on, everything updates as for a link
 *   (the page reloads into the prices when the modal closes); if not, it says so and the discount stays as it was.
 *   Black Friday tiers (settings meerkat_sms_code, meerkat_drop_code; blank turns them off): a phone number moves the
 *   visitor up to the text bonus code, kept on the device like any link offer, and a signed-in customer counts as
 *   signed up (with a phone on file, as having given it). The color drop's code stays on the cart beside the sale's:
 *   it only covers the current drop's colors, so typing or linking it never marks the whole site down. The care and
 *   tools code (meerkat_gear_code) stays on the cart the same way. A drop color's product page offers the drop's
 *   dollars, and a care or tool product's page its percent. The sale page (sections/sale-hero.liquid) signs up through signUpEmail()
 *   and addPhone(), and the modal doesn't open by itself there.
 *   AI try-on gate (aura-tryon.js): the gate's own email form calls signUpEmail() and its code form applyCode(). An
 *   email sets mkt_email and a working code mkt_code; an email or phone fires `aura:lead` and an unlock or code
 *   `aura:unlock` on document, as the footer signup (aura-unlock.js) and the chat (aura-chat.js) do, so the held
 *   render goes ahead.
 * The localStorage keys are today's (mkt, lmkt, amkt, mkt_hidden, mkt_phone, anon_mkt_list, startDate,
 * futureDate), so visitors carry over. window.showMeerkat(), window.hideAllMeerkat() and any
 * [data-meerkat-open] element work as before.
 */
(() => {
  const ls = {
    get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
    del: (k) => { try { localStorage.removeItem(k); } catch (e) { /* private mode */ } }
  };
  const ss = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  };
  const json = (raw, fallback) => { try { return raw ? JSON.parse(raw) : fallback; } catch (e) { return fallback; } };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (str, vars = {}) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (vars[k] != null ? vars[k] : m));
  const aura = () => window.aura || { track() {} };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const pad = (n) => String(n).padStart(2, '0');

  const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const UNLOCK = 'aura.discount.v1';

  /* Product pages seen before sign-up, sent under the new id */
  function flushAnon(api, id) {
    const list = json(ls.get('anon_mkt_list'), null);
    if (!Array.isArray(list)) return;
    ls.del('anon_mkt_list');
    list.forEach((uri) => post(`${api}/events/meerkat`, { id, uri }).catch(() => {}));
  }

  /* ---------- visitor events (today's footer script) ---------- */
  function visitorEvents(cfg) {
    const api = cfg.api;
    const fromLink = new URLSearchParams(location.search).get('mkt');
    if (fromLink) ls.set('mkt', fromLink);

    if (cfg.customer) {
      if (!ls.get('lmkt')) {
        post(`${api}/events/user`, Object.assign({}, cfg.customer, { id: ls.get('mkt') }))
          .then((r) => r.json())
          .then((r) => { if (r && r.id) ls.set('lmkt', r.id); })
          .catch(() => {});
      }
    } else if (!ls.get('amkt')) {
      ls.set('amkt', uuid());
    }

    const seen = (uri) => {
      const id = ls.get('mkt');
      if (id) { post(`${api}/events/meerkat`, { id, uri }).catch(() => {}); return; }
      if (!uri.includes('product')) return;
      const list = json(ls.get('anon_mkt_list'), []);
      const queue = (Array.isArray(list) ? list : []).concat(uri).slice(-5);
      ls.set('anon_mkt_list', JSON.stringify(queue));
    };
    // Cart lines as today's script names them: the product title before " - ", hyphenated. Only once the visitor
    // has an id: today's script never queues them, so anon_mkt_list keeps to product pages actually seen
    if (ls.get('mkt')) (cfg.cart_titles || []).forEach((title) => seen(`product/${String(title).split('-')[0].trim().toLowerCase().replace(/ /g, '-')}`));
    seen(location.href.slice(location.origin.length));

    if (cfg.customer && cfg.customer.email && (cfg.cart_items || []).length) {
      setTimeout(() => {
        post(`${api}/events/cart`, {
          email: cfg.customer.email, phone: cfg.customer.phone, id: ls.get('mkt') || ls.get('amkt'), cartItems: cfg.cart_items
        }).catch(() => {});
      }, 1000);
    }

    // Today's script also records #contact_form, which is Shopify's default id for the newsletter form, not the contact
    // page (that one is #ContactForm and never went to the list). The footer signup does this now (aura-unlock.js)

    // Explorer links carry the id, so the explorer can credit shares and renders
    const id = ls.get('mkt') || ls.get('lmkt') || ls.get('amkt');
    let host = null;
    try { host = new URL(cfg.explorer_url).host; } catch (e) { /* no explorer */ }
    if (!id || !host) return;
    document.querySelectorAll('a[href]').forEach((a) => {
      try {
        const u = new URL(a.href);
        if (u.host !== host || u.searchParams.has('mkt')) return;
        u.searchParams.set('mkt', id);
        a.href = u.href;
      } catch (e) { /* not a URL */ }
    });
  }

  /* ---------- the sale: "Month D" dates, as the holiday list and today's stored window use ---------- */
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const WINDOW_DAYS = 3;
  const dayLabel = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  function parseDay(str, year) {
    const m = /^([a-z]+)\.?\s+(\d{1,2})\b/i.exec(String(str || '').trim());
    const month = m ? MONTHS.findIndex((n) => n.slice(0, 3).toLowerCase() === m[1].slice(0, 3).toLowerCase()) : -1;
    return month < 0 ? null : new Date(year, month, Number(m[2]));
  }
  /* A start and end around today; one that runs over New Year ends in the next year */
  function span(start, end, today) {
    const y = today.getFullYear();
    const a = parseDay(start, y);
    const b = parseDay(end, y);
    if (!a || !b) return null;
    if (b < a) { if (today < a) a.setFullYear(y - 1); else b.setFullYear(y + 1); }
    return { start: a, end: b };
  }

  /* The holiday list and the link-only offers, fetched once a session */
  let saleFetch = null;
  function saleData(api) {
    if (saleFetch) return saleFetch;
    saleFetch = (async () => {
      const cached = json(ss.get('aura.meerkat.hollist.v2'), null);
      if (cached && Array.isArray(cached.list)) return cached;
      try {
        const res = await (await fetch(`${api}/hollist`)).json();
        if (Array.isArray(res.list)) {
          const data = { list: res.list, offers: Array.isArray(res.offers) ? res.offers : [] };
          ss.set('aura.meerkat.hollist.v2', JSON.stringify(data));
          return data;
        }
      } catch (e) { /* the month's special */ }
      return { list: [], offers: [] };
    })();
    return saleFetch;
  }
  const holidays = (api) => saleData(api).then((d) => d.list);

  /* Shopify's t filter HTML-escapes ("isn&#39;t"); the strings go in as text */
  function decodeAll(obj) {
    const ta = document.createElement('textarea');
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => { ta.innerHTML = String(v); return [k, ta.value]; }));
  }

  const sameCode = (a, b) => !!a && String(a).trim().toLowerCase() === String(b || '').trim().toLowerCase();

  /* Any other code, as Shopify has it (the API's GET /discount/:code, once a session per code): an offer from today
     to the code's end (30 days when it has none). undefined when the API can't be reached, null when the code isn't
     active (unknown, ended or used up) */
  async function lookupCode(api, code, today) {
    const key = `aura.code.${code.trim().toLowerCase()}`;
    let r = json(ss.get(key), null);
    if (!r) {
      try {
        const res = await fetch(`${api}/discount/${encodeURIComponent(code.trim())}`);
        if (!res.ok) return undefined;
        r = await res.json();
        ss.set(key, JSON.stringify(r));
      } catch (e) { return undefined; }
    }
    if (!r || !r.active) return null;
    const end = r.endsAt ? new Date(r.endsAt) : new Date(+today + 30 * 864e5);
    end.setHours(0, 0, 0, 0);
    return { h: { code: r.code || code, amountOff: Number(r.amountOff) || 0, percentOff: Number(r.percentOff) || 0 }, range: { start: today, end } };
  }

  /* The code this visit's prices follow: ?discount=CODE, else the one this device kept from an earlier link. One of
     the link-only offers counts between its dates; any other code is looked up. Today's sale code needs neither
     (the caller handles it). `fresh` when it came from the link just now */
  const LINK_OFFER = 'aura.link_offer.v1';
  async function linkOffer(api, offers, today, saleCode, dropCode) {
    const inOffers = (code) => {
      const h = offers.find((o) => sameCode(code, o.code) && Number(o.amountOff) > 0);
      const range = h && span(h.start, h.end, today);
      return range && today >= range.start && today <= range.end ? { h, range } : null;
    };
    const kept = (json(ls.get(LINK_OFFER), null) || {}).code;
    for (const [code, fresh] of [[new URLSearchParams(location.search).get('discount'), true], [kept, false]]) {
      if (!code || !String(code).trim() || sameCode(code, saleCode) || sameCode(code, dropCode)) continue;
      const found = inOffers(code) || (await lookupCode(api, String(code), today));
      if (found === undefined) return null; // API down: leave the device as it is
      if (found) {
        ls.set(LINK_OFFER, JSON.stringify({ code: found.h.code }));
        return Object.assign(found, { fresh });
      }
      if (!fresh) ls.del(LINK_OFFER);
    }
    return null;
  }

  const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
  /* The holiday on today and its dates (the last match wins, as today) */
  function holidayOn(list, today) {
    let found = null;
    list.forEach((h) => {
      const s = span(h.start, h.end, today);
      if (s && today >= s.start && today <= s.end) found = { h, range: s };
    });
    return found;
  }

  /* Today's shipping notice: the holiday on today when it has shippingNotice. The text is its shippingText without
     the trailing link; the ship date is the "ship on" date in that text, or the day after the holiday ends */
  function shippingNotice(on) {
    if (!on || !on.h.shippingNotice) return null;
    const h = on.h;
    const start = dayLabel(on.range.start);
    const end = dayLabel(on.range.end);
    const after = new Date(on.range.end);
    after.setDate(after.getDate() + 1);
    let text = String(h.shippingText || '').replace(/[:\s]*https?:\/\/\S+\s*$/, '').trim();
    const shipOn = /\bships?\s+on\s+([a-z]+\.?\s+\d{1,2})\b/i.exec(text);
    const shipDate = shipOn ? shipOn[1] : dayLabel(after);
    if (!text) text = `Special orders placed between ${start} - ${end} will ship on ${shipDate}`;
    if (!/[.!?]$/.test(text)) text += '.';
    return {
      name: h.name || '', code: h.code || '', amount: h.amountOff ? `$${h.amountOff}` : '',
      start, end, ship_date: shipDate, text
    };
  }

  /* The shipping-notification page (sections/shipping-notice.liquid) */
  function paintNoticePage(notice) {
    document.querySelectorAll('[data-shipping-notice]').forEach((root) => {
      root.removeAttribute('aria-busy');
      root.querySelector('[data-notice-off]').hidden = !!notice;
      root.querySelector('[data-notice-on]').hidden = !notice;
      if (!notice) return;
      const vars = {};
      Object.keys(notice).forEach((k) => { vars[k] = esc(notice[k]); });
      root.querySelectorAll('[data-notice-fill]').forEach((el) => { el.innerHTML = fill(el.innerHTML, vars); });
      const sale = root.querySelector('[data-notice-sale]');
      if (sale) sale.hidden = !(notice.code && notice.amount);
      root.querySelector('[data-notice-text]').textContent = notice.text;
    });
  }

  function currentSale(list, cfg, link) {
    const today = startOfToday();
    // A link offer wins unless today's sale is bigger
    const during = (holidayOn(list, today) || { h: {} }).h;
    if (link && Number(link.h.amountOff) >= Number(during.amountOff || cfg.amount || 50)) {
      return {
        key: `link:${link.h.code}`,
        // A looked-up code has no name: it reads as today's holiday, or the month's special
        holiday: !!(link.h.name || during.name),
        name: link.h.name || during.name || MONTHS[today.getMonth()],
        emoji: link.h.emoji || during.emoji || '🚀',
        amount: Number(link.h.amountOff),
        code: link.h.code,
        notice: false,
        // A code is a fixed amount until it ends: no dwindling deal
        plain: true,
        end: link.range.end,
        endLabel: dayLabel(link.range.end),
        todayOnly: +today === +link.range.end
      };
    }
    const on = holidayOn(list, today);
    const special = on ? on.h : null;
    let range = on ? on.range : null;
    if (special) {
      ls.del('startDate');
      ls.del('futureDate');
    } else {
      // The visitor's 3-day window; like today's script it starts again on its end date
      range = span(ls.get('startDate'), ls.get('futureDate'), today);
      if (!range || today < range.start || today >= range.end) {
        const end = new Date(today);
        end.setDate(end.getDate() + WINDOW_DAYS);
        ls.set('startDate', dayLabel(today));
        ls.set('futureDate', dayLabel(end));
        range = { start: today, end };
      }
    }
    // In a month's last 3 days the special is next month's
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const month = MONTHS[(today.getMonth() + (today.getDate() > lastDay - WINDOW_DAYS ? 1 : 0)) % 12];
    return {
      key: special ? special.name : (cfg.sale_name || 'monthly'),
      holiday: !!special || !!cfg.sale_name,
      name: special ? special.name : (cfg.sale_name || month),
      emoji: (special && special.emoji) || cfg.sale_emoji || '🚀',
      amount: Number((special && special.amountOff) || cfg.amount) || 50,
      code: (special && special.code) || cfg.code,
      notice: !!(special && special.shippingNotice),
      end: range.end,
      endLabel: dayLabel(range.end),
      todayOnly: +today === +range.end
    };
  }

  /* ---------- sign-up ---------- */
  const EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const PHONES = [
    /^\+?(\d{1,3})?[-. ]?\(?\d{1,3}\)?[-. ]?\d{1,4}[-. ]?\d{1,4}[-. ]?\d{1,9}$/,
    /^\+?(\d{1,3})?[-. ]?(\(\d{3}\)|\d{3})[-. ]?\d{3}[-. ]?\d{4}$/
  ];
  const TYPOS = {
    'gmai.com': 'gmail.com', 'gmail.comm': 'gmail.com', 'gmial.com': 'gmail.com', 'gmailcom': 'gmail.com', 'gmal.com': 'gmail.com',
    'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yhoo.com': 'yahoo.com', 'yahoo.comm': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmal.com': 'hotmail.com', 'homail.com': 'hotmail.com', 'hotmail.comm': 'hotmail.com',
    'outllok.com': 'outlook.com', 'outlook.comm': 'outlook.com', 'otlook.com': 'outlook.com'
  };
  function fixEmail(email) {
    const at = email.lastIndexOf('@');
    if (at < 1) return email;
    const domain = email.slice(at + 1).toLowerCase();
    return `${email.slice(0, at)}@${TYPOS[domain] || domain.replace(/\.(con|comu|comm)$/, '.com')}`;
  }

  /* Card transform that puts it over a rect (the discount button) */
  function toward(card, rect) {
    const dx = rect.left + rect.width / 2 - (card.left + card.width / 2);
    const dy = rect.top + rect.height / 2 - (card.top + card.height / 2);
    return `translate(${dx}px, ${dy}px) scale(${Math.max(0.1, rect.width / card.width)})`;
  }
  const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

  class AuraMeerkat extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.cfg = json((this.querySelector('[data-meerkat-config]') || {}).textContent, {});
      this.cfg.api = String(this.cfg.api || '').replace(/\/$/, '');
      this.t = decodeAll(this.cfg.strings || {});
      if (this.cfg.events) visitorEvents(this.cfg);
      // The offer band (sections/offer-band.liquid), the cart's "There is currently a shipping notice"
      // (snippets/cart-summary.liquid) and the shipping-notification page follow today's holiday's notice, for shop
      // accounts too, who get no sign-up. The flag stays for the session so later pages show the band at once
      // (layout/theme.liquid)
      const hollist = holidays(this.cfg.api);
      hollist.then((list) => {
        const notice = shippingNotice(holidayOn(list, startOfToday()));
        document.documentElement.classList.toggle('has-shipping-notice', !!notice);
        ss.set('aura.shipping_notice', notice ? '1' : '0');
        paintNoticePage(notice);
      });
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) {
        window.showMeerkat = () => {};
        window.hideAllMeerkat = () => {};
        return;
      }
      this.card = this.querySelector('[data-meerkat-card]');
      // Black Friday tiers on: signed in counts as signed up, and a phone on the account as having given it
      this.tiers = !!(this.cfg.sms_code || this.cfg.drop_code);
      if (this.tiers && this.cfg.customer && this.cfg.customer.phone) ls.set('mkt_phone', 'true');
      this.hasPhone = !!ls.get('mkt_phone');
      // The footer signup unlocks through this element (aura-unlock.js)
      this.canUnlock = true;
      this.unlocked = !!this.cfg.unlock || !!ls.get(UNLOCK) || this.hasPhone || !!ls.get('aura.unlock.v1') || (this.tiers && !!this.cfg.customer);
      this.price = this.cfg.price;
      // The buy box's variant (its id field changes with the length tiles, the select and the buy bar's switcher)
      document.addEventListener('change', (e) => {
        const prices = this.cfg.prices || {};
        const id = e.target && e.target.name === 'id' ? e.target.value : null;
        if (!id || !(id in prices) || prices[id] === this.price) return;
        this.price = prices[id];
        if (this.sale) this.paint();
      });
      window.showMeerkat = () => this.open('link');
      window.hideAllMeerkat = () => this.hideAll();
      document.addEventListener('click', (e) => {
        const codeOpener = e.target.closest('[data-meerkat-code]');
        if (codeOpener) { e.preventDefault(); this.openCode(codeOpener); return; }
        const opener = e.target.closest('[data-meerkat-open]');
        if (!opener) return;
        e.preventDefault();
        this.open('link', opener);
      });
      this.bind();
      this.loaded = saleData(this.cfg.api).then(async ({ list, offers }) => {
        const regular = currentSale(list, this.cfg, null);
        // A phone already given keeps its bonus code, unless the device holds another offer
        if (this.hasPhone && this.cfg.sms_code && !json(ls.get(LINK_OFFER), null)) ls.set(LINK_OFFER, JSON.stringify({ code: this.cfg.sms_code }));
        const link = await linkOffer(this.cfg.api, offers, startOfToday(), regular.code, this.cfg.drop_code);
        this.sale = link ? currentSale(list, this.cfg, link) : regular;
        this.paint();
        // From a link with a code that's on (today's sale, a link-only offer or any active dollars-off code): marked
        // down at once at the better of it and today's sale. A percent code just goes on the cart
        const param = new URLSearchParams(location.search).get('discount');
        if (sameCode(param, this.cfg.drop_code) && window.aura && window.aura.applyDiscount) window.aura.applyDiscount(this.cfg.drop_code);
        if (link && link.fresh) ls.set('mkt_code', 'true');
        if (link && link.fresh && !link.h.amountOff && window.aura && window.aura.applyDiscount) window.aura.applyDiscount(link.h.code);
        if ((link && link.fresh && link.h.amountOff) || sameCode(param, regular.code)) { this.fromLink(); return; }
        if (this.unlocked) { this.keepUnlocked(); return; }
        this.mountFab();
        this.schedule();
      });
      document.addEventListener('aura:cart', (e) => { if (this.unlocked && this.sale) this.syncCodes(e.detail && e.detail.data); });
    }

    bind() {
      this.dialog.addEventListener('click', (e) => {
        // The dialog fills the screen; a click outside the card lands on it. Kept from aura.js, which would close at once
        if (e.target === this.dialog) { e.stopPropagation(); this.close('backdrop'); return; }
        if (e.target.closest('[data-meerkat-close]')) this.close('close');
        else if (e.target.closest('[data-meerkat-copy]')) this.copy();
      });
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close('esc'); });
      this.dialog.addEventListener('close', () => this.onClosed());
      this.querySelector('[data-meerkat-email]').addEventListener('submit', (e) => this.submitEmail(e));
      this.querySelector('[data-meerkat-phone]').addEventListener('submit', (e) => this.submitPhone(e));
      this.querySelectorAll('[data-meerkat-promo-form]').forEach((form) => form.addEventListener('submit', (e) => this.submitCode(e)));
      this.querySelectorAll('.input').forEach((input) => input.addEventListener('input', () => this.invalid(input, false)));
    }

    /* What's on offer here: under small_under (a product page's picked variant), the partial code's percent off;
       otherwise the sale's dollars off a full roll */
    offer() {
      const cfg = this.cfg;
      const under = Number(cfg.small_under) || 0;
      if (cfg.product_gear && cfg.gear_code) return { small: true, gear: true, amount: `${cfg.gear_percent}%`, code: cfg.gear_code };
      if (cfg.partial_code && under && this.price != null && this.price < under * 100) {
        return { small: true, amount: `${cfg.partial_percent}%`, code: cfg.partial_code };
      }
      if (cfg.product_drop && cfg.drop_code) return { small: false, amount: `$${cfg.drop_amount}`, code: cfg.drop_code };
      return { small: false, amount: `$${this.sale.amount}`, code: this.sale.code };
    }

    paint() {
      const s = this.sale;
      const t = this.t;
      const o = this.offer();
      const amount = o.amount;
      const full = `$${s.amount}`;
      const half = `$${Math.round(s.amount * 0.5)}`;
      const under = this.cfg.under_label;
      const set = (sel, text) => this.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
      const saleTitle = this.cfg.sale_title;
      set('[data-meerkat-sale]', saleTitle ? `${s.emoji} ${saleTitle}`.trim() : fill(s.holiday ? t.sale : t.special, { emoji: s.emoji, name: s.name, month: s.name }));
      set('[data-meerkat-amount]', fill(t.amount, { amount }));
      set('[data-meerkat-qual]', o.gear ? t.gear_qual : (o.small ? fill(t.small_qual, { under }) : (saleTitle ? t.sale_qual : t.full_roll)));
      set('[data-meerkat-get]', saleTitle && !o.small ? fill(t.get_sale, { name: s.name }) : fill(t.get_code, { amount }));
      // Already on the sale page: the link would go nowhere
      if (document.querySelector('aura-sale-page')) this.querySelectorAll('[data-meerkat-sale-link]').forEach((el) => { el.hidden = true; });
      set('[data-meerkat-code]', o.code);
      this.querySelectorAll('[data-meerkat-notice]').forEach((el) => { el.hidden = !s.notice; });
      this.querySelectorAll('[data-meerkat-unlocked]').forEach((el) => { el.hidden = !this.unlocked; });
      // The code step names the other code: the partial code under a full-roll offer, and the other way round
      const partial = this.querySelector('[data-meerkat-partial]');
      const fullLine = this.querySelector('[data-meerkat-full-line]');
      if (partial) partial.hidden = o.small;
      if (fullLine) {
        fullLine.hidden = !o.small;
        fullLine.innerHTML = fill(t.full_line, { code: `<b>${esc(s.code)}</b>`, amount: esc(full) });
      }
      this.labelFabs();

      const sub = this.querySelector('[data-meerkat-sub]');
      const decay = this.querySelector('[data-meerkat-decay]');
      const countdown = this.querySelector('[data-meerkat-countdown]');
      sub.hidden = false;
      decay.hidden = false;
      countdown.hidden = false;
      if (o.small) {
        // A percent doesn't dwindle: no countdown, and the line says what it covers
        sub.textContent = fill(o.gear ? t.gear_sub : t.small_sub, { amount: full });
        decay.hidden = true;
        countdown.hidden = true;
        set('[data-meerkat-code-sub]', fill(o.gear ? t.gear_code_sub : t.small_code_sub, { percent: amount, under }));
      } else if (this.cfg.countdown && !s.plain) {
        sub.textContent = s.todayOnly ? t.sub_today : t.sub_dwindle;
        const step = (label, amt, now) => `<div class="meerkat__decay-step${now ? ' is-now' : ''}"><span class="lbl">${esc(label)}</span><span class="mono meerkat__decay-amt">${esc(fill(t.amount, { amount: amt }))}</span></div>`;
        decay.innerHTML = s.todayOnly
          ? step(t.today_only, amount, true)
          : `${step(t.order_today, amount, true)}<svg class="icon meerkat__decay-arrow" aria-hidden="true" focusable="false"><use href="#i-arrow-right"></use></svg>${step(s.endLabel, half, false)}`;
        set('[data-meerkat-code-sub]', fill(s.todayOnly ? t.code_sub_today : t.code_sub, { amount, date: s.endLabel, half }));
      } else {
        sub.hidden = !this.tiers;
        if (this.tiers) sub.textContent = fill(t.bf_sub, { sms: `$${this.cfg.sms_amount}`, drop: `$${this.cfg.drop_amount}` });
        decay.hidden = true;
        countdown.hidden = true;
        set('[data-meerkat-code-sub]', fill(t.code_sub_plain, { amount }));
      }

      // The text bonus: what a phone number adds to today's sale
      const bonus = Number(this.cfg.sms_amount) - Number(s.amount);
      if (this.cfg.sms_code && bonus > 0) {
        set('[data-meerkat-phone-label]', fill(t.phone_bonus, { bonus: `$${bonus}` }));
        set('[data-meerkat-phone-btn]', fill(t.text_me_bonus, { bonus: `$${bonus}` }));
      }
      set('[data-meerkat-bonus]', fill(t.bonus_on, { amount: full }));
      this.querySelectorAll('[data-meerkat-bonus]').forEach((el) => { el.hidden = !(this.cfg.sms_code && sameCode(s.code, this.cfg.sms_code)); });

      // Already signed up for texts: the code step without the phone form
      if (this.hasPhone) {
        this.querySelector('[data-meerkat-phone]').hidden = true;
        this.querySelector('[data-meerkat-phone-skip]').textContent = this.querySelector('.meerkat__close').getAttribute('aria-label');
      }
    }

    /* The discount button sits in the Ask Aura dock, so it follows the dock's place on every template and hides
       with it (chat open, omnibox open, cart page on phones): above the bar on desktop, beside the chat button
       on phones. Product pages also get one in the mobile buy bar, which takes over from the dock's there */
    mountFab() {
      const tpl = this.querySelector('template[data-meerkat-fab]');
      if (!tpl) return;
      const make = () => {
        const fab = tpl.content.firstElementChild.cloneNode(true);
        fab.addEventListener('click', () => this.open('button', fab));
        return fab;
      };
      this.fab = make();
      let dock = document.querySelector('.ai-dock');
      if (!dock) {
        dock = document.createElement('div');
        dock.className = 'ai-dock';
        document.body.append(dock);
      }
      dock.prepend(this.fab);
      this.fabs = [this.fab];
      document.querySelectorAll('[data-meerkat-slot]').forEach((slot) => {
        const fab = make();
        fab.classList.replace('btn--primary', 'btn--secondary');
        fab.classList.add('meerkat-fab--bar');
        slot.replaceWith(fab);
        this.fabs.unshift(fab);
      });
      this.labelFabs();
    }

    labelFabs() {
      const amount = this.offer().amount;
      (this.fabs || []).forEach((fab) => {
        fab.querySelector('[data-meerkat-fab-text]').textContent = fill(this.t.fab, { amount });
        fab.setAttribute('aria-label', fill(this.t.fab_label, { amount }));
      });
    }

    schedule() {
      if (this.hasPhone) return;
      const views = Number(ss.get('aura.meerkat.views') || 0) + 1;
      ss.set('aura.meerkat.views', String(views));
      if (location.search.includes('showModal')) { this.open('url'); return; }
      const mode = this.cfg.auto_open;
      const quiet = ls.get('mkt_hidden') || mode === 'never' || (window.Shopify && Shopify.designMode)
        || document.querySelector('aura-sale-page')
        || (mode === 'polite' && (views < 2 || ['product', 'cart', 'search'].includes(this.cfg.page_type)));
      if (quiet) { this.showFab(false); return; }
      setTimeout(() => {
        if (this.dialog.open || this.off || this.unlocked || ls.get('mkt_hidden')) return;
        // Never on top of another dialog (cart drawer, menu, explorer, chat): the button pops in instead
        if (document.querySelector('dialog[open]')) { this.showFab(true); return; }
        this.open('auto');
      }, Math.max(0, Number(this.cfg.delay) || 0) * 1000);
    }

    /* Where the discount button on screen is (or would be), for the card to grow out of and shrink into */
    fabRect() {
      if (this.unlocked) return null;
      for (const fab of this.fabs || []) {
        if (!fab.isConnected) continue;
        const hidden = fab.hidden;
        fab.hidden = false;
        const r = fab.getBoundingClientRect();
        const seen = r.width && r.top < window.innerHeight && r.bottom > 0 && (!fab.checkVisibility || fab.checkVisibility({ visibilityProperty: true }));
        fab.hidden = hidden;
        if (seen) return r;
      }
      return null;
    }

    showFab(pop) {
      if (!this.fabs || this.off || this.unlocked) return;
      this.fabs.forEach((fab) => {
        fab.hidden = false;
        if (!pop || reduceMotion()) return;
        fab.classList.remove('is-pop');
        void fab.offsetWidth;
        fab.classList.add('is-pop');
      });
    }

    hideFabs() { (this.fabs || []).forEach((fab) => { fab.hidden = true; }); }

    async open(trigger, opener) {
      await this.loaded;
      if (this.dialog.open) return;
      if (!this.step) this.show(this.hasPhone || this.unlocked ? 'phone' : 'email');
      const from = this.fabRect();
      this.hideFabs();
      this.dialog._opener = opener || document.activeElement;
      this.dialog.showModal();
      // Focus the headline, not the close button: it reads the offer first and shows no ring on auto-open
      const heading = this.querySelector(`[data-step="${this.step}"] h2`);
      if (heading) heading.focus();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
      aura().track('aura_modal_shown', { trigger, step: this.step, sale: this.sale.key, offer: this.offer().small ? 'small' : 'full' });
      if (reduceMotion()) return;
      const card = this.card.getBoundingClientRect();
      this.card.animate(from
        ? [{ transform: toward(card, from), opacity: 0.4 }, { transform: 'none', opacity: 1 }]
        : [{ transform: 'translateY(14px) scale(0.97)', opacity: 0 }, { transform: 'none', opacity: 1 }],
      { duration: from ? 480 : 450, easing: EASE });
    }

    close(reason) {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      this.closeReason = reason;
      const to = this.done || this.off ? null : this.fabRect();
      if (!to || reduceMotion()) { this.dialog.close(); return; }
      this.dialog.classList.add('is-closing');
      const card = this.card.getBoundingClientRect();
      this.card.animate([{ transform: 'none', opacity: 1 }, { transform: toward(card, to), opacity: 0 }], { duration: 420, easing: EASE, fill: 'forwards' })
        .finished.catch(() => {}).then(() => { if (this.dialog.open) this.dialog.close(); });
    }

    onClosed() {
      this.dialog.classList.remove('is-closing');
      this.card.getAnimations().forEach((a) => a.cancel());
      clearInterval(this.timer);
      ls.set('mkt_hidden', 'true');
      aura().track('aura_modal_closed', { step: this.step, reason: this.closeReason || 'esc', sale: this.sale.key });
      this.closeReason = null;
      // Just unlocked here: reload into the marked-down prices once the cart has the amount
      if (this.reloadOnClose) { this.reloadOnClose = false; Promise.resolve(this.reloadAfter || this.unlocking).then(() => location.reload()); return; }
      if (!this.done) this.showFab(true);
    }

    hideAll() {
      this.off = true;
      this.hideFabs();
      if (this.dialog.open) { this.closeReason = 'hidden'; this.dialog.close(); }
    }

    show(step, focus) {
      this.step = step;
      this.querySelectorAll('[data-step]').forEach((el) => { el.hidden = el.dataset.step !== step; });
      if (focus) { const h = this.querySelector(`[data-step="${step}"] h2`); if (h) h.focus(); }
    }

    tick() {
      const el = this.querySelector('[data-meerkat-timer]');
      if (!el || !this.cfg.countdown) return;
      const end = new Date(this.sale.end);
      end.setHours(23, 59, 59, 0);
      const diff = end - Date.now();
      if (diff <= 0) { el.textContent = this.t.last_chance; return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      el.textContent = d >= 1 ? `${d}D ${pad(h)}H ${pad(m)}M` : `${pad(h)}:${pad(m)}:${pad(s)}`;
    }

    invalid(input, on) {
      if (on) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid');
      const error = document.getElementById(input.getAttribute('aria-describedby'));
      if (error) error.hidden = !on;
    }

    /* Each sign-up waits for the one before, so the phone lands on the id the email got */
    signup(fields) {
      const api = this.cfg.api;
      this.pending = (this.pending || Promise.resolve()).then(() => {
        const id = ls.get('mkt');
        return post(`${api}/events/user`, id ? Object.assign({ id }, fields) : fields)
          .then((r) => r.json())
          .then((res) => {
            if (!res || !res.id) return;
            ls.set('mkt', res.id);
            flushAnon(api, res.id);
            // An email we already have a phone for: no need to ask, and the bonus is theirs
            if (res.phone && fields.email && this.tiers && !this.hasPhone) {
              ls.set('mkt_phone', 'true');
              this.hasPhone = true;
              this.phoneDone = this.smsBonus('known_phone');
              if (this.sale) this.paint();
            }
          })
          .catch(() => {});
      });
    }

    submitEmail(e) {
      e.preventDefault();
      const input = this.querySelector('#MeerkatEmail');
      const value = input.value.trim();
      if (!EMAIL.test(value)) { this.invalid(input, true); input.focus(); return; }
      this.invalid(input, false);
      if (!this.unlocked) this.reloadOnClose = true;
      this.signUpEmail(value, 'modal');
      this.show('phone', true);
    }

    /* An email from anywhere on the page (the modal above, the AI try-on's gate in aura-tryon.js): onto the list,
       the discount unlocked, and `aura:lead` for anything waiting on a lead. Resolves { ok } (false for a bad email);
       `done` settles once the discount is on the cart. The caller decides whether the page reloads into the prices */
    signUpEmail(raw, surface) {
      const value = String(raw || '').trim();
      if (!EMAIL.test(value)) return { ok: false };
      this.signup({ email: fixEmail(value) });
      ls.set('mkt_hidden', 'true');
      ls.set('mkt_email', 'true');
      aura().track('aura_modal_signup', { field: 'email', sale: this.sale && this.sale.key, surface });
      const done = this.unlock(surface);
      this.lead('email', surface);
      return { ok: true, done };
    }

    /* An email or phone given: anything waiting on a lead (the AI try-on's gate, aura-tryon.js) can go ahead.
       `reload` when closing the modal reloads the page into the marked-down prices */
    lead(field, surface = 'modal') {
      document.dispatchEvent(new CustomEvent('aura:lead', { detail: { field, surface, reload: !!this.reloadOnClose } }));
    }

    /* Email given (here, or the footer signup): mark prices down site-wide. The amount goes on the cart for
       snippets/unlock-price.liquid, the code that fits the cart goes on too, and the discount buttons go for
       good. Resolves once both are on the cart; the caller reloads into the new prices */
    unlock(surface) {
      if (this.unlocking) return this.unlocking;
      this.unlocked = true;
      ls.set(UNLOCK, ls.get(UNLOCK) || '{}');
      document.documentElement.classList.add('is-unlocked');
      this.hideFabs();
      // Anything waiting on the discount (the AI try-on's gate, aura-tryon.js) can go ahead
      document.dispatchEvent(new CustomEvent('aura:unlock', { detail: { surface } }));
      this.unlocking = this.loaded.then(() => {
        this.paint();
        aura().track('aura_discount_unlocked', { surface, sale: this.sale.key });
        return Promise.all([this.setUnlock(String(this.sale.amount)), this.syncCodes()]);
      });
      return this.unlocking;
    }

    /* Every page for an unlocked visitor: no button, no auto-open, and the cart keeps today's amount (a holiday
       changes it; a finished order starts a new cart without it, so the next page is marked down again) */
    keepUnlocked() {
      ls.set(UNLOCK, ls.get(UNLOCK) || '{}');
      document.documentElement.classList.add('is-unlocked');
      const amount = String(this.sale.amount);
      const changed = this.cfg.unlock !== amount;
      return Promise.all([changed ? this.setUnlock(amount) : null, this.syncCodes()]).then(() => {
        // The sale page is all prices: reload into them once when they moved (a signed-in visitor's first page)
        if (changed && document.querySelector('aura-sale-page') && !ss.get('aura.sale.reloaded')) {
          ss.set('aura.sale.reloaded', '1');
          location.reload();
        }
      });
    }

    /* Landed from an offer link: prices go down site-wide at once, as an email does, at the offer's amount and with
       its code on the cart. The page reloads into the new prices once (once a session per code, so never a loop) */
    fromLink() {
      const before = this.cfg.unlock;
      const done = this.unlocked ? this.keepUnlocked() : this.unlock('link');
      const once = `aura.link_offer.reloaded.${this.sale.code}`;
      Promise.resolve(done).then(() => {
        if (this.cfg.unlock === before || ss.get(once)) return;
        ss.set(once, '1');
        location.reload();
      });
    }

    /* Cart writes one at a time: on a visitor's first write Shopify makes the cart, and two at once would make
       two carts, the cookie keeping only one of them */
    queue(fn) {
      this.cartQueue = (this.cartQueue || Promise.resolve()).then(fn).catch(() => { /* the next page tries again */ });
      return this.cartQueue;
    }

    /* The cart attribute snippets/unlock-price.liquid reads */
    setUnlock(amount) {
      const routes = (aura().config || {}).routes || {};
      return this.queue(() => fetch(`${routes.cart_update || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _aura_unlock: amount } })
      }).then((r) => { if (r.ok) this.cfg.unlock = amount; }));
    }

    /* The codes on the cart for the unlocked discount, as offer() splits them on a product page: the sale's
       full-roll code when a full roll ($400 and up) is in the cart, or nothing is yet, with the color drop's code
       beside it (each covers its own colors; the one with nothing to cover just waits, applicable: false); else the
       partial code's percent for small items. The care and tools code goes with either. Never the partial code with
       the full-roll ones. Our other codes come off, an
       old holiday's too; codes the shopper typed stay. Runs after every cart change (aura:cart); aura.applyDiscount
       skips wholesale carts */
    syncCodes(cart) {
      return this.queue(async () => {
        const site = window.aura;
        if (!site || !site.applyDiscount) return;
        let data = cart;
        if (!data || !Array.isArray(data.items) || !Array.isArray(data.discount_codes)) {
          const routes = (site.config || {}).routes || {};
          data = await (await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } })).json();
        }
        const full = this.sale.code;
        const partial = this.cfg.partial_code;
        const under = (Number(this.cfg.small_under) || 0) * 100;
        const lines = data.items.filter((i) => !i.gift_card && i.original_price > 0);
        const drop = this.cfg.drop_code;
        const gear = this.cfg.gear_code;
        const small = partial && lines.length && !lines.some((i) => i.original_price >= under);
        const want = (small ? [partial, gear] : [full || partial, drop, gear]).filter(Boolean);
        if (!want.length) return;
        const saved = json(ls.get(UNLOCK), {}) || {};
        const ours = [full, partial, drop, gear].concat(saved.codes || []).filter(Boolean).map((c) => String(c).toLowerCase());
        const codes = data.discount_codes.map((d) => String(d.code).toLowerCase());
        const wanted = want.map((c) => c.toLowerCase());
        const stale = codes.filter((c) => ours.includes(c) && !wanted.includes(c));
        ls.set(UNLOCK, JSON.stringify(Object.assign(saved, { codes: [full, partial, drop, gear].filter(Boolean) })));
        const missing = want.filter((c) => !codes.includes(c.toLowerCase()));
        if (!missing.length && !stale.length) return;
        for (const code of missing.length ? missing : [want[0]]) await site.applyDiscount(code, { replace: stale });
      });
    }

    /* The cart's "Have a different code?": the code step with its code form open */
    async openCode(opener) {
      await this.loaded;
      if (!this.dialog.open && this.step !== 'email' && this.step !== 'phone') this.show(this.unlocked || this.hasPhone ? 'phone' : 'email');
      await this.open('code', opener);
      const promo = this.querySelector(`[data-step="${this.step}"] [data-meerkat-promo]`);
      if (!promo) return;
      promo.open = true;
      promo.querySelector('input').focus();
    }

    /* A typed code (the modal's "Have a code?", and the footer signup's through aura-unlock.js), checked like a
       ?discount= link (lookupCode). A dollars-off code that's on takes over if it's at least what this visitor
       already gets, and they're unlocked either way; a percent code goes on the cart; anything else changes
       nothing. Resolves { ok, text } for the caller to show, plus `changed` (prices move: reload into them once
       `done` settles) and `unlockedNow` */
    async applyCode(raw, surface) {
      const t = this.t;
      const code = String(raw || '').trim().toUpperCase();
      if (!/^[A-Z0-9_-]{2,40}$/.test(code)) return { ok: false, text: t.code_invalid };
      await this.loaded;
      // The color drop's code only covers the drop's colors, so it goes on the cart and marks nothing down
      if (sameCode(code, this.cfg.drop_code)) {
        if (window.aura && window.aura.applyDiscount) await window.aura.applyDiscount(this.cfg.drop_code);
        return { ok: true, text: fill(t.code_cart, { code }) };
      }
      const today = startOfToday();
      const { list, offers } = await saleData(this.cfg.api);
      const regular = currentSale(list, this.cfg, null);
      const offer = offers.find((o) => sameCode(code, o.code) && Number(o.amountOff) > 0);
      const range = offer && span(offer.start, offer.end, today);
      const found = sameCode(code, regular.code) ? null
        : (range && today >= range.start && today <= range.end ? { h: offer, range } : await lookupCode(this.cfg.api, code, today));
      if (found === undefined) return { ok: false, retry: true, text: t.code_error };
      if (!found && !sameCode(code, regular.code)) {
        aura().track('aura_code_entered', { surface, ok: false, sale: this.sale.key });
        return { ok: false, text: t.code_invalid };
      }
      aura().track('aura_code_entered', { surface, ok: true, kind: found && !found.h.amountOff ? 'percent' : 'amount', sale: this.sale.key });
      // A working code counts like an email for the AI try-on's gate (aura-tryon.js)
      ls.set('mkt_code', 'true');
      document.dispatchEvent(new CustomEvent('aura:unlock', { detail: { surface, code } }));
      if (found && !found.h.amountOff) {
        // The partial code, once unlocked, is ours: syncCodes puts it on carts of small items only
        if (this.unlocked && sameCode(found.h.code, this.cfg.partial_code)) {
          return { ok: true, text: fill(t.small_code_sub, { percent: `${this.cfg.partial_percent}%`, under: this.cfg.under_label }) };
        }
        if (window.aura && window.aura.applyDiscount) await window.aura.applyDiscount(found.h.code);
        return { ok: true, text: fill(t.code_cart, { code: found.h.code }) };
      }
      // Dollars off (today's sale code, a link-only offer or a looked-up code)
      const wins = sameCode(code, this.sale.code) || (found ? Number(found.h.amountOff) : Number(regular.amount)) >= Number(this.sale.amount);
      if (wins && !sameCode(code, this.sale.code)) {
        if (found) ls.set(LINK_OFFER, JSON.stringify({ code: found.h.code }));
        else ls.del(LINK_OFFER);
        this.sale = found ? currentSale(list, this.cfg, found) : regular;
      }
      const before = this.cfg.unlock;
      const unlockedNow = !this.unlocked;
      const done = Promise.resolve(unlockedNow ? this.unlock(surface) : this.keepUnlocked());
      this.paint();
      return {
        ok: true,
        text: fill(wins ? t.code_applied : t.code_lower, { code, amount: `$${this.sale.amount}` }),
        changed: String(this.sale.amount) !== String(before),
        done,
        unlockedNow
      };
    }

    /* The modal's "Have a code?" form: the answer under the field; a code that moves prices reloads into them
       when the modal closes */
    async submitCode(e) {
      e.preventDefault();
      const form = e.currentTarget;
      const input = form.querySelector('input');
      const button = form.querySelector('button');
      const say = (text, tone, where = form) => {
        const msg = where.querySelector('[data-meerkat-promo-msg]');
        msg.textContent = text;
        msg.hidden = !text;
        msg.classList.toggle('is-ok', tone === 'ok');
        msg.classList.toggle('is-error', tone === 'error');
      };
      button.disabled = true;
      say(this.t.code_checking);
      const r = await this.applyCode(input.value, 'modal');
      button.disabled = false;
      if (!r.ok) {
        if (!r.retry) input.setAttribute('aria-invalid', 'true');
        say(r.text, 'error');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      if (r.unlockedNow) { say(''); this.show('phone'); }
      const promo = this.querySelector(`[data-step="${this.step}"] [data-meerkat-promo]`);
      if (promo) promo.open = true;
      say(r.text, 'ok', promo || form);
      if (r.changed) { this.reloadAfter = r.done; this.reloadOnClose = true; }
    }

    submitPhone(e) {
      e.preventDefault();
      const input = this.querySelector('#MeerkatPhone');
      const r = this.addPhone(input.value, 'modal');
      if (!r.ok) { this.invalid(input, true); input.focus(); return; }
      this.invalid(input, false);
      this.show('thanks', true);
      r.done.then((bonus) => {
        if (!bonus || !bonus.changed) return;
        this.reloadAfter = bonus.done;
        this.reloadOnClose = true;
      });
    }

    /* A phone number from anywhere on the page (the modal above, the sale page): onto the list, and the text bonus if
       one is on. Resolves { ok } (false for a bad number); `done` settles once the bonus is on the cart */
    addPhone(raw, surface) {
      const value = String(raw || '').trim();
      if (!PHONES.some((re) => re.test(value))) return { ok: false };
      // The sale's text list: that sale's drop texts go only to people who gave their phone for it
      this.signup({ phone: value, phoneOffer: this.cfg.sms_code || undefined });
      ls.set('mkt_phone', 'true');
      this.hasPhone = true;
      this.done = true;
      aura().track('aura_modal_signup', { field: 'phone', sale: this.sale && this.sale.key, surface });
      const done = this.smsBonus(surface);
      this.lead('phone', surface);
      return { ok: true, done };
    }

    /* The visitor moves up to the text bonus code, as if they had typed it (applyCode). Null when there isn't one */
    async smsBonus(surface) {
      await this.loaded;
      const code = this.cfg.sms_code;
      if (!code || sameCode(this.sale.code, code)) return null;
      const r = await this.applyCode(code, surface);
      if (r.done) await r.done;
      this.paint();
      return r;
    }

    copy() {
      const code = this.offer().code;
      const label = this.querySelector('[data-meerkat-copy-label]');
      if (!this.copyLabel) this.copyLabel = label.textContent;
      const done = () => {
        label.textContent = this.t.copied;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => { label.textContent = this.copyLabel; }, 1800);
      };
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.cssText = 'position:fixed;opacity:0';
        this.dialog.append(ta);
        ta.select();
        try { if (document.execCommand('copy')) done(); } catch (err) { /* nothing to copy with */ }
        ta.remove();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, fallback);
      else fallback();
    }
  }

  /* A prerendered page starts once the shopper opens it, so its visit isn't sent and the modal's timers don't run
     unseen (theme.liquid window.auraShown) */
  (window.auraShown || Promise.resolve()).then(() => {
    if (!customElements.get('aura-meerkat')) customElements.define('aura-meerkat', AuraMeerkat);
  });
})();
